import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Edit as EditIcon, Clear as ClearIcon } from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useAuth } from '../../../../services/auth/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CallbacksService from '../../../../services/api/callbacks';
import Navbar from '../../../../components/shared-menu/Navbar';
import { useWebSocket } from '../../../../services/websocket/WebSocketProvider';

// Status options
const STATUS_OPTIONS = {
  'Pending': 'pending',
  'In Progress': 'in_progress',
  'Contacted': 'contacted',
  'Enquiry Created': 'enquiry_created',
  'Not Interested': 'not_interested'
};

// Source options
const SOURCE_OPTIONS = {
  'Website': 'website',
  'Soft In Principle': 'soft_in_principle'
};

// Status color mapping
const getStatusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'in_progress':
      return 'info';
    case 'contacted':
      return 'primary';
    case 'enquiry_created':
      return 'success';
    case 'not_interested':
      return 'error';
    default:
      return 'default';
  }
};

// Source color mapping
const getSourceColor = (source) => {
  switch (source) {
    case 'website':
      return 'primary';
    case 'soft_in_principle':
      return 'secondary';
    default:
      return 'default';
  }
};

export default function CallbacksPage() {
  const { setIsAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Get id from URL params
  const idFromUrl = searchParams.get('id');

  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // DataGrid pagination and sorting
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  
  const [sortModel, setSortModel] = useState([
    {
      field: 'created_at',
      sort: 'desc',
    },
  ]);

  const [filterModel, setFilterModel] = useState({
    items: idFromUrl ? [
      {
        field: 'id',
        operator: 'equals',
        value: idFromUrl,
      }
    ] : [],
  });
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCallback, setSelectedCallback] = useState(null);
  const [modalStatus, setModalStatus] = useState('');
  const [modalNotes, setModalNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  // WebSocket integration for real-time updates
  useWebSocket({
    onMessage: (messageData) => {
      // console.log('📩 WebSocket message received in Callbacks:', messageData);
      
      if (messageData.notification_type === 'callback') {
        const newCallback = messageData.additional_data?.callback;
        if (newCallback) {
          // Play notification sound
          const audio = new Audio('/audio/message-notification-tone.mp3');
          audio.play().catch((error) => console.error("⚠ Callback Notification Error", error));
          
          // Add the new callback to the existing list if we're on the first page
          // and sorted by created_at descending (most recent first)
          if (paginationModel.page === 0 && 
              sortModel[0]?.field === 'created_at' && 
              sortModel[0]?.sort === 'desc') {
            
            setCallbacks(prevCallbacks => {
              // Check if callback already exists to avoid duplicates
              const existingCallback = prevCallbacks.find(cb => cb.id === newCallback.id);
              if (existingCallback) {
                return prevCallbacks;
              }
              
              // Add new callback at the beginning
              const updatedCallbacks = [newCallback, ...prevCallbacks];
              
              // // If we have more items than pageSize, remove the last one
              // if (updatedCallbacks.length > paginationModel.pageSize) {
              //   updatedCallbacks.pop();
              // }

              paginationModel.pageSize++;
              
              return updatedCallbacks;
            });
            
            // Update total records count
            setTotalRecords(prev => prev + 1);
          } else {
            // If not on first page or different sorting, just update total count
            // The user will see the new callback when they navigate back to first page
            setTotalRecords(prev => prev + 1);
          }
        }
      }
    }
  });

  const fetchCallbacks = async () => {
    try {
      setLoading(true);
      
      const queryParams = {
        page: paginationModel.page + 1,
        rows: paginationModel.pageSize,
        sortField: sortModel[0]?.field || 'created_at',
        sortOrder: sortModel[0]?.sort === 'desc' ? -1 : 1,
      };

      // Add filters
      if (filterModel.items.length > 0) {
        const filters = {};
        filterModel.items.forEach(item => {
          if (item.value !== undefined && item.value !== null && item.value !== '') {
            filters[item.field] = {
              value: item.value,
              matchMode: item.operator === 'equals' ? 'equals' : 'contains'
            };
          }
        });
        if (Object.keys(filters).length > 0) {
          queryParams.filters = JSON.stringify(filters);
        }
      }

      const response = await CallbacksService.getCallbacks(
        queryParams,
        setIsAuthenticated,
        navigate
      );

      if (response.success) {
        setCallbacks(response.data);
        setTotalRecords(response.totalRecords);
      } else {
        setError('Failed to fetch callbacks');
      }
    } catch (err) {
      console.error('Error fetching callbacks:', err);
      setError('An error occurred while fetching callbacks');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (callback) => {
    setSelectedCallback(callback);
    setModalStatus(callback.status);
    setModalNotes(callback.notes || '');
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCallback(null);
    setModalStatus('');
    setModalNotes('');
  };

  const handleUpdate = async () => {
    if (!selectedCallback) return;

    try {
      setUpdating(true);
      
      const updateData = { 
        status: modalStatus,
        notes: modalNotes 
      };

      const response = await CallbacksService.updateCallbackStatus(
        selectedCallback.id,
        updateData,
        setIsAuthenticated,
        navigate
      );

      if (response.success) {
        // Refresh the data
        fetchCallbacks();
        handleModalClose();
      } else {
        setError('Failed to update callback');
      }
    } catch (err) {
      setError('An error occurred while updating callback');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleString();
    } catch (error) {
      return '-';
    }
  };

  // Clear filters function
  const clearFilters = () => {
    setFilterModel({ items: [] });
    setSearchParams({});
  };

  // Responsive column configuration
  const getResponsiveColumns = () => {
    const baseColumns = [
      {
        field: 'id',
        headerName: 'ID',
        width: isMobile ? 50 : 60,
        minWidth: 40,
        flex: isMobile ? 0.3 : 0.4,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'whatsapp_number',
        headerName: isMobile ? 'Phone' : 'WhatsApp Number',
        width: isMobile ? 110 : 140,
        minWidth: 90,
        flex: isMobile ? 1.2 : 1,
        filterable: true,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'whatsapp_name',
        headerName: isMobile ? 'Name' : 'WhatsApp Name',
        width: isMobile ? 90 : 130,
        minWidth: 70,
        flex: isMobile ? 1 : 0.9,
        filterable: true,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'source',
        headerName: 'Source',
        width: isMobile ? 70 : 100,
        minWidth: 60,
        flex: isMobile ? 0.7 : 0.8,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={Object.keys(SOURCE_OPTIONS).find(key => SOURCE_OPTIONS[key] === params.value) || params.value}
            color={getSourceColor(params.value)}
            size="small"
            sx={{ 
              fontSize: isMobile ? '0.65rem' : '0.75rem',
              height: isMobile ? 18 : 22
            }}
          />
        ),
        type: 'singleSelect',
        valueOptions: Object.entries(SOURCE_OPTIONS).map(([label, value]) => ({
          value: value,
          label: label
        })),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: isMobile ? 80 : 110,
        minWidth: 70,
        flex: isMobile ? 0.8 : 0.9,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={Object.keys(STATUS_OPTIONS).find(key => STATUS_OPTIONS[key] === params.value) || params.value}
            color={getStatusColor(params.value)}
            size="small"
            sx={{ 
              fontSize: isMobile ? '0.65rem' : '0.75rem',
              height: isMobile ? 18 : 22
            }}
          />
        ),
        type: 'singleSelect',
        valueOptions: Object.entries(STATUS_OPTIONS).map(([label, value]) => ({
          value: value,
          label: label
        })),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: isMobile ? 50 : 70,
        minWidth: 50,
        flex: isMobile ? 0.4 : 0.5,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            variant="outlined"
            size="small"
            startIcon={!isMobile ? <EditIcon /> : null}
            onClick={() => handleEditClick(params.row)}
            sx={{ 
              minWidth: 'auto',
              fontSize: isMobile ? '0.65rem' : '0.75rem',
              padding: isMobile ? '2px 6px' : '4px 8px'
            }}
          >
            {isMobile ? <EditIcon fontSize="small" /> : 'Edit'}
          </Button>
        ),
      },
    ];

    // Add conditional columns for larger screens
    if (!isMobile) {
      baseColumns.splice(5, 0, {
        field: 'assigned_to',
        headerName: 'Assigned To',
        width: isTablet ? 90 : 120,
        minWidth: 70,
        flex: 0.7,
        filterable: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (!params.value) return '-';
          return params.value;
        },
      });

      baseColumns.splice(6, 0, {
        field: 'notes',
        headerName: 'Notes',
        width: isTablet ? 100 : 140,
        minWidth: 80,
        flex: 0.9,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (!params.value) {
            return (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontStyle: 'italic', fontSize: isTablet ? '0.7rem' : '0.8rem' }}
              >
                No notes
              </Typography>
            );
          }
          
          return (
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                fontSize: isTablet ? '0.7rem' : '0.8rem'
              }}
              title={params.value}
            >
              {params.value}
            </Typography>
          );
        },
        filterable: true,
      });
    }

    // Add date columns for tablet and desktop
    if (!isMobile) {
      baseColumns.splice(-1, 0, {
        field: 'created_at',
        headerName: 'Created At',
        width: isTablet ? 120 : 140,
        minWidth: 100,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => formatDate(params.value),
        type: 'dateTime',
        valueGetter: (value) => {
          if (!value) return null;
          const date = new Date(value);
          return isNaN(date.getTime()) ? null : date;
        },
      });

      // Add updated_at column only for desktop (not tablet)
      if (!isTablet) {
        baseColumns.splice(-1, 0, {
          field: 'updated_at',
          headerName: 'Updated At',
          width: 140,
          minWidth: 100,
          flex: 0.8,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => formatDate(params.value),
          type: 'dateTime',
          valueGetter: (value) => {
            if (!value) return null;
            const date = new Date(value);
            return isNaN(date.getTime()) ? null : date;
          },
        });
      }
    }

    return baseColumns;
  };

  useEffect(() => {
    fetchCallbacks();
  }, [paginationModel, sortModel, filterModel]);

  // Update filters when URL params change
  useEffect(() => {
    const newIdFromUrl = searchParams.get('id');
    const currentIdFilter = filterModel.items.find(item => item.field === 'id');
    
    if (newIdFromUrl !== currentIdFilter?.value) {
      setFilterModel(prev => ({
        items: newIdFromUrl 
          ? [
              ...prev.items.filter(item => item.field !== 'id'),
              {
                field: 'id',
                operator: 'equals',
                value: newIdFromUrl,
              }
            ]
          : prev.items.filter(item => item.field !== 'id')
      }));
    }
  }, [searchParams]);

  if (loading && callbacks.length === 0) {
    return (
        <Box display="flex" flexDirection="column" height="100vh">
          <Navbar />
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <CircularProgress size={50} />
          </Box>
        </Box>
    );
  }

  return (
      <Box display="flex" flexDirection="column" height="100vh">
        <Navbar />
        
        <Box 
          p={isMobile ? 1 : isTablet ? 2 : 3} 
          flex={1} 
          overflow="hidden"
          sx={{ 
            width: '100%',
            maxWidth: '100vw'
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box 
            display="flex" 
            flexDirection={isMobile ? 'column' : 'row'}
            justifyContent="space-between" 
            alignItems={isMobile ? 'flex-start' : 'center'} 
            mb={2}
            gap={isMobile ? 1 : 0}
          >
            <Typography 
              variant={isMobile ? "h5" : "h4"}
              sx={{ fontSize: isMobile ? '1.25rem' : undefined }}
            >
              Callback Requests
            </Typography>
            <Button 
              variant="outlined" 
              onClick={clearFilters}
              size={isMobile ? "small" : "medium"}
              startIcon={<ClearIcon />}
              sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
            >
              Clear Filters
            </Button>
          </Box>

          <Paper 
            sx={{ 
              height: isMobile ? 'calc(100vh - 200px)' : isTablet ? 650 : 750, 
              width: '100%',
              overflow: 'hidden'
            }}
          >
            <DataGrid
              rows={callbacks}
              columns={getResponsiveColumns()}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              filterModel={filterModel}
              onFilterModelChange={setFilterModel}
              loading={loading}
              pageSizeOptions={isMobile ? [5, 10] : [5, 10, 25, 50]}
              rowCount={totalRecords}
              paginationMode="server"
              sortingMode="server"
              filterMode="server"
              slots={{
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                  sx: {
                    '& .MuiButton-root': {
                      fontSize: isMobile ? '0.7rem' : '0.8rem',
                      padding: isMobile ? '4px 8px' : undefined
                    }
                  }
                },
              }}
              disableColumnFilter={false}
              disableColumnSelector={isMobile}
              disableDensitySelector={isMobile}
              columnVisibilityModel={isMobile ? {
                assigned_to: false,
                notes: false,
                created_at: false,
                updated_at: false
              } : isTablet ? {
                updated_at: false
              } : {}}
              sx={{
                '& .MuiDataGrid-toolbarContainer': {
                  padding: isMobile ? 1 : 2,
                  minHeight: isMobile ? 40 : 56,
                  '& .MuiButtonBase-root': {
                    fontSize: isMobile ? '0.7rem' : undefined
                  }
                },
                '& .MuiDataGrid-columnHeaders': {
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  minHeight: isMobile ? 40 : 56,
                  '& .MuiDataGrid-columnHeader': {
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }
                },
                '& .MuiDataGrid-cell': {
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  padding: isMobile ? '4px 8px' : '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  '&:first-of-type': {
                    paddingLeft: '8px'
                  }
                },
                '& .MuiDataGrid-footerContainer': {
                  minHeight: isMobile ? 40 : 52,
                  '& .MuiTablePagination-root': {
                    fontSize: isMobile ? '0.7rem' : undefined
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: isMobile ? '0.7rem' : undefined
                  }
                },
                backgroundColor: 'transparent',
                border: 'none',
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none'
                },
                '& .MuiDataGrid-virtualScroller': {
                  marginTop: '0px !important'
                }
              }}
              autoHeight={false}
            />
          </Paper>

          {/* Edit Modal */}
          <Dialog 
            open={modalOpen} 
            onClose={handleModalClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                margin: isMobile ? 0 : undefined,
                maxHeight: isMobile ? '100vh' : undefined
              }
            }}
          >
            <DialogTitle sx={{ fontSize: isMobile ? '1.1rem' : undefined }}>
              Edit Callback Request #{selectedCallback?.id}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ fontSize: isMobile ? '0.9rem' : undefined }}
                  >
                    Customer Details
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="textSecondary"
                    sx={{ fontSize: isMobile ? '0.8rem' : undefined, mb: 0.5 }}
                  >
                    <strong>WhatsApp:</strong> {selectedCallback?.whatsapp_number}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="textSecondary"
                    sx={{ fontSize: isMobile ? '0.8rem' : undefined, mb: 0.5 }}
                  >
                    <strong>Name:</strong> {selectedCallback?.whatsapp_name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="textSecondary"
                    sx={{ fontSize: isMobile ? '0.8rem' : undefined }}
                  >
                    <strong>Source:</strong> {selectedCallback?.source}
                  </Typography>
                </Box>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ fontSize: isMobile ? '0.9rem' : undefined }}>
                    Status
                  </InputLabel>
                  <Select
                    value={modalStatus}
                    label="Status"
                    onChange={(e) => setModalStatus(e.target.value)}
                    sx={{ fontSize: isMobile ? '0.9rem' : undefined }}
                  >
                    {Object.entries(STATUS_OPTIONS).map(([label, value]) => (
                      <MenuItem key={value} value={value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={label}
                            color={getStatusColor(value)}
                            size="small"
                            sx={{ fontSize: isMobile ? '0.7rem' : undefined }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={isMobile ? 3 : 4}
                  value={modalNotes}
                  onChange={(e) => setModalNotes(e.target.value)}
                  placeholder="Enter notes for this callback request..."
                  InputLabelProps={{
                    sx: { fontSize: isMobile ? '0.9rem' : undefined }
                  }}
                  InputProps={{
                    sx: { fontSize: isMobile ? '0.9rem' : undefined }
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ padding: isMobile ? 2 : undefined }}>
              <Button 
                onClick={handleModalClose}
                disabled={updating}
                size={isMobile ? "small" : "medium"}
                sx={{ fontSize: isMobile ? '0.8rem' : undefined }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdate}
                variant="contained"
                disabled={updating}
                size={isMobile ? "small" : "medium"}
                startIcon={updating ? <CircularProgress size={16} /> : null}
                sx={{ fontSize: isMobile ? '0.8rem' : undefined }}
              >
                {updating ? 'Updating...' : 'Update'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
  );
}